import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../../lib/api'
import type { ProfileListItem, JobListItem } from '../../types/api'
import Badge from '../../components/ui/Badge'
import { JobTypeBadge, JobStatusBadge } from '../Jobs'

export default function CandidateDashboard() {
  const navigate = useNavigate()
  const [profiles, setProfiles] = useState<ProfileListItem[]>([])
  const [jobs, setJobs] = useState<JobListItem[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([api.profiles.list(), api.jobs.list()])
      .then(([p, j]) => {
        setProfiles(p.data)
        setJobs(j.data.filter((job) => job.type !== 'Exclusive' && job.status === 'Open').slice(0, 6))
      })
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <p className="text-text-muted text-sm">Loading...</p>

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div>
        <h2 className="font-heading text-sm tracking-tactical text-text-muted mb-4">WHO ARE YOU?</h2>
        <div className="space-y-2">
          {profiles.length === 0 ? (
            <p className="text-text-muted text-sm">No profiles yet.</p>
          ) : (
            profiles.map((p) => (
              <button
                key={p.id}
                onClick={() => navigate(`/candidate/profile/${p.id}`)}
                className="w-full text-left bg-surface-low border border-border-subtle p-4 hover:border-primary transition-colors group"
              >
                <p className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">{p.name}</p>
                {p.skills.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {p.skills.slice(0, 4).map((s) => <Badge key={s} variant="muted">{s}</Badge>)}
                    {p.skills.length > 4 && <span className="text-xs text-text-muted">+{p.skills.length - 4}</span>}
                  </div>
                )}
              </button>
            ))
          )}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-sm tracking-tactical text-text-muted">AVAILABLE JOBS</h2>
          <button
            onClick={() => navigate('/candidate/jobs')}
            className="text-xs text-primary hover:underline tracking-tactical"
          >
            VIEW ALL →
          </button>
        </div>
        <div className="space-y-2">
          {jobs.length === 0 ? (
            <p className="text-text-muted text-sm">No open jobs available.</p>
          ) : (
            jobs.map((job) => (
              <div key={job.id} className="bg-surface-low border border-border-subtle p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-foreground">{job.title}</p>
                    <p className="text-xs text-text-muted mt-0.5">{job.employer}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <JobTypeBadge type={job.type} />
                    <JobStatusBadge status={job.status} />
                  </div>
                </div>
                {job.required_topics.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {job.required_topics.slice(0, 3).map((t) => <Badge key={t} variant="required">{t}</Badge>)}
                    {job.required_topics.length > 3 && <span className="text-xs text-text-muted">+{job.required_topics.length - 3}</span>}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}
